import React, { useState, useMemo, useCallback } from 'react';
import Marquee, { Motion, randomIntFromInterval } from 'react-marquee-slider';
import { useSpring, config, animated } from 'react-spring';
import PropTypes from 'prop-types';
import Icon from '../../components/Icon';

import './Marquee.scss';

const MarqueeContainer = ({ scale, children, ...props }) => {
  const opacityStyles = useSpring({
    config: { ...config.stiff },
    from: { opacity: 0 },
    to: { opacity: 1 },
  });

  return (
    <animated.div
      className="marquee-container"
      style={{ height: `${scale * 200}px`, ...opacityStyles }}
      {...props}
    >
      {children}
    </animated.div>
  );
};

const CircleContainer = ({ scale, children, ...props }) => {
  const zoomOpacityStyles = useSpring({
    config: { ...config.stiff },
    from: { opacity: 0, scale: 0 },
    to: { opacity: 1, scale: 1 },
  });

  return (
    <animated.div
      className="circle-container"
      style={{
        width: `${scale * 75}px`,
        height: `${scale * 75}px`,
        ...zoomOpacityStyles,
      }}
      {...props}
    >
      {children}
    </animated.div>
  );
};

const Circle = ({ scale, children, ...props }) => (
  <div
    className="circle"
    style={{
      width: `${scale * 150}px`,
      height: `${scale * 150}px`,
      fontSize: `${scale * 80}px`,
    }}
    {...props}
  >
    {children}
  </div>
);

function MarqueeComp({
  data,
  marqueeVelocity,
  motionVelocity,
  minScale,
  resetAfterTries,
  scatterRandomly,
  ...props
}) {
  const [scale, setScale] = useState(false);

  const handleMarquee = (e) => {
    const _window = e ? e.currentTarget : window;
    if (_window && _window.innerWidth > 1400) setScale(1);
    else if (_window && _window.innerWidth > 1100) setScale(0.8);
    else if (_window && _window.innerWidth > 800) setScale(0.65);
    else setScale(0.5);
  };

  const handleResizeMarquee = useCallback((e) => {
    const handler = setTimeout(() => handleMarquee(e), 1000);
    return () => clearTimeout(handler);
  }, []);

  useMemo(() => {
    window.addEventListener('resize', handleResizeMarquee);

    return () => {
      // return a cleanup function to unregister our function since its gonna run multiple times
      window.removeEventListener('resize', handleResizeMarquee);
    };
  }, [handleResizeMarquee]);

  useMemo(() => {
    setTimeout(() => {
      handleMarquee();
    }, 500);
  }, []);

  return (
    <MarqueeContainer scale={scale || 1}>
      {scale && (
        <Marquee
          velocity={scale * marqueeVelocity}
          minScale={minScale}
          resetAfterTries={resetAfterTries}
          scatterRandomly={scatterRandomly}
          {...props}
        >
          {data.map((lang, idx) => (
            <Motion
              key={idx}
              velocity={scale * motionVelocity}
              initDeg={randomIntFromInterval(0, 360)}
              direction={Math.random() > 0.5 ? 'clockwise' : 'counterclockwise'}
              radius={scale * 100}
            >
              <CircleContainer scale={scale}>
                <Circle scale={scale} title={lang.name}>
                  <Icon type={lang.type} icon={lang.icon} className="colored" />
                </Circle>
              </CircleContainer>
            </Motion>
          ))}
        </Marquee>
      )}
    </MarqueeContainer>
  );
}

MarqueeComp.defaultProps = {
  marqueeVelocity: 25,
  motionVelocity: 10,
  minScale: 0.7,
  resetAfterTries: 200,
  scatterRandomly: false,
  direction: 'rtl',
  data: [],
};

MarqueeComp.propTypes = {
  marqueeVelocity: PropTypes.number,
  motionVelocity: PropTypes.number,
  minScale: PropTypes.number,
  resetAfterTries: PropTypes.number,
  scatterRandomly: PropTypes.bool,
  direction: PropTypes.string,
  data: PropTypes.arrayOf(PropTypes.object),
};

export default React.memo(MarqueeComp);
